import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { voiceNavItems } from '@/config/voiceNav'
import { cn } from '@/utils/cn'

function NavLinkStyles({ isActive }) {
  return cn(
    'relative whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors',
    'after:absolute after:bottom-0 after:left-3 after:right-3 after:h-0.5 after:rounded-full after:transition-all after:duration-200',
    isActive
      ? 'text-foreground after:bg-primary'
      : 'text-muted-foreground after:bg-transparent hover:text-foreground hover:after:bg-border/80'
  )
}

export default function VoiceNav() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!mobileOpen) return
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileOpen(false)
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [mobileOpen])

  const mobilePanel =
    mobileOpen &&
    typeof document !== 'undefined' &&
    createPortal(
      <div className="fixed inset-0 z-[100] md:hidden" role="dialog" aria-modal="true" aria-label="Menu de navegação">
        <button
          type="button"
          className="absolute inset-0 bg-background/70 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
          aria-label="Fechar menu"
        />
        <div className="absolute inset-y-0 right-0 flex w-[min(100%,20rem)] flex-col border-l border-border bg-card shadow-2xl">
          <div className="flex h-14 shrink-0 items-center justify-between border-b border-border px-4">
            <span className="text-sm font-semibold tracking-tight text-foreground">Navegar</span>
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground touch-manipulation"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav id="voice-mobile-nav" className="flex-1 overflow-y-auto p-3" aria-label="Seções">
            <ul className="flex flex-col gap-0.5">
              {voiceNavItems.map((item) => {
                const Icon = item.icon
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      end={item.end === true}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        cn(
                          'flex min-h-[48px] items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] font-medium transition-colors touch-manipulation',
                          isActive
                            ? 'bg-primary/10 text-foreground'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        )
                      }
                    >
                      <Icon className="h-5 w-5 shrink-0 opacity-80" aria-hidden />
                      <span className="leading-tight">{item.label}</span>
                    </NavLink>
                  </li>
                )
              })}
            </ul>
          </nav>
        </div>
      </div>,
      document.body
    )

  return (
    <>
      <nav className="hidden min-w-0 md:flex md:items-center md:gap-0.5" aria-label="Principal">
        {voiceNavItems.map((item) => (
          <NavLink key={item.path} to={item.path} end={item.end === true} className={NavLinkStyles}>
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="flex md:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-background text-foreground shadow-sm transition-colors hover:bg-muted touch-manipulation"
          aria-expanded={mobileOpen}
          aria-controls="voice-mobile-nav"
          aria-label="Abrir menu"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      {mobilePanel}
    </>
  )
}
